import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import I18n from '../../../helper/Localization';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { getTheme } from '@fluentui/react';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { postFormDataToApi } from '../../../helper/ApiHelper';
import { Label } from 'office-ui-fabric-react/lib/Label';
import { msalApp } from '../../../helper/AuthHelper';
import { Panel, PanelType } from 'office-ui-fabric-react/lib/Panel';
import { BusySpinner } from '../../busyIndicators/BusySpinner';
import { BasicDialog } from '../BasicDialog';
import { ManualTestStatusDialog } from './ManualTestStatusDialog';
import { FileUploader } from 'react-drag-drop-files';
import { Colors } from '../../../styles/Globals';
import { Icon } from '@fluentui/react/lib/Icon';

const Headline = styled.div`
    font-size: 26px;
    font-weight: bold;
    color: ${(props) => props.fontColor};
`;

const SubHeadline = styled.div`
    font-size: 14px;
    font-weight: bold;
    color: ${(props) => props.fontColor};
`;

const InputHeadlineContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`;

const InputContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    min-height: 75px;
`;

const PanelHeader = styled.div`
    display: flex;
    width: 100%;
    padding: 20px;
    justify-content: flex-start;
    flex-direction: column;
`;

const PanelBody = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100%;
    height: 100%;
    padding: 0 20px;
    min-width: max-content;
`;

const PanelFooter = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding: 10px 20px;
    position: relative;
`;

const MessageWrapper = styled.div`
    position: absolute;
    left: 0;
    top: -20px;
    width: 100%;
    visibility: ${(props) => (props.hidden ? 'hidden' : 'visible')};
`;

const FlexEnd = styled.div`
    display: flex;
    padding-top: 5px;
    justify-content: flex-end;
`;

const FileUploadContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin: 20px 0;
    min-height: 75px;
`;

const CustomFileUploadWrapper = styled.div`
    .file-uploader {
        border: 2px dashed ${(props) => props.themePrimary};
        border-radius: 8px;
        padding: 10px 10px;
        background-color: ${Colors.lightGray};
        transition: all 0.3s ease;
        cursor: pointer;
        min-height: 80px;
        max-width: 390px;
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        justify-content: flex-start;
        gap: 20px;

        &:hover {
            background-color: ${Colors.hoverLightGray};
        }

        &.drag-active {
            border-color: #0078d4;
            background-color: #f3f9ff;
        }
    }

    .upload-icon {
        font-size: 48px;
        margin-bottom: 10px;
        flex-shrink: 0;
    }

`;

const DeleteFileWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: end;
    gap: 4px;
    cursor: pointer;
    padding-right: 5px;
    font-weight: 500;
    color: ${(props) => props.fontColor};
    transition: opacity 0.3s ease;
        
    &:hover {
        opacity: 0.7;
    }

    .delete-info {
        text-decoration: underline;
    }
`;

const DisplayUploadedFileInfo = styled.div`
    border-radius: 8px;
    padding: 10px 5px 10px 5px;
    background-color: ${Colors.lightGray};
    transition: all 0.3s ease;
    min-height: 80px;
    max-width: 390px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 15px;
    color: ${(props) => props.fontColor};
    font-size: 14px;
    font-weight: bold;
    margin-bottom: 0;
    flex: 1;
`;

const SelectedFileInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    color: ${(props) => props.fontColor};
    font-size: 14px;
    font-weight: bold;
    padding-top: 18px;
    margin-bottom: 0;
    flex: 1;
`;

const DisplayFileInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    color: ${(props) => props.fontColor};
    font-size: 14px;
    font-weight: bold;
    padding-top: 2px;
    margin-bottom: 0;
    flex: 1;
`;

const DisplayFileProperties = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-top: 4px;

    .file-size-info {
        display: flex;
        align-items: center;
        gap: 4px;
        color: ${Colors.inactiveGray};
        font-size: 12px;
        font-weight: 500;
    }
`;

const UploadText = styled.div`
    color: ${(props) => props.fontColor};
    font-size: 14px;
    font-weight: bold;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 310px;

    .upload-text-browse {
        color: ${(props) => props.themePrimary};
        border-bottom: 2px solid ${(props) => props.themePrimary};
    }
`;

const UploadSubText = styled.div`
    color: ${Colors.inactiveGray};
    font-size: 12px;
    font-weight: 500;
    margin-bottom: 0;
    margin-top: 4px;
`;

/**
 * Dialog to submit a manual test result with PDF file upload.
 */
export const ManualTestPdfDialog = (props) => {
    /**
     * The state of the test date.
     */
    const [dateExecuted, setDateExecuted] = useState(new Date());
    /**
     * visible state for the busy indicator
     */
    const [isBusy, setIsBusy] = useState(false);
    /**
     * The state of the order position id.
     */
    const [orderPositionId, setOrderPositionId] = useState('');
    /**
     * The state of the order position number.
     */
    const [orderPositionNumber, setOrderPositionNumber] = useState('');
    /**
     * The state of the wearer name.
     */
    const [wearerName, setWearerName] = useState('');
    /**
     * The state of the left success result.
     */
    const [resultLeft, setResultLeft] = useState('');
    /**
     * The state of the right success result.
     */
    const [resultRight, setResultRight] = useState('');
    /**
     * Error message for the MessageBar
     */
    const [errorMessage, setErrorMessage] = useState('');
    /**
     * Success message for the MessageBar
     */
    const [successMessage, setSuccessMessage] = useState('');
    /**
     * Uploaded PDF file state
     */
    const [uploadedPdfFile, setUploadedPdfFile] = useState(null);

    /**
     * Whether the abort dialog is open or not.
     */
    const [isAbortDialogOpen, setIsAbortDialogOpen] = useState(false);

    useEffect(() => {
        setWearerName(props.name);
        setOrderPositionId(props.orderPositionId);
        setOrderPositionNumber(props.orderPositionNumber);
        setDateExecuted(new Date());
        props.resetParentIsBusy();
    }, [props.name, props.orderPositionId, props.isOpen, props]);

    const theme = getTheme();
    const cancelButtonStyles = {
        root: {
            border: 'unset',
        },
    };

    const fileTypes = ["PDF"];

    /**
     * Resets the states and then closes the dialog.
     */
    const closeDialog = () => {
        setIsAbortDialogOpen(false);
        setErrorMessage('');
        setResultLeft('');
        setResultRight('');
        setSuccessMessage('');
        setUploadedPdfFile(null);
        setIsBusy(false);
        props.toggleHideDialog();
    };

    /**
     * Handler to set the date executed.
     * @param date the new date.
     */
    const dateExecutedHandler = (date) => {
        setDateExecuted(date);
    };

    /**
     * Handler for PDF file upload
     * @param file the uploaded file
     */
    const handlePdfUpload = (file) => {
        setErrorMessage('');
        setUploadedPdfFile(file);
        console.log('file:', file);
        console.log('PDF file uploaded:', file.name);
    };

    const handlePdfMaxSizeError = (file) => {
        console.log('Error PDF file uploaded:', file.name);
        setErrorMessage(I18n.get().t('ManualTestPdfUploadDialog_MaxSizeError'));
        setUploadedPdfFile(null);
    }

    /**
     * Handler to remove the uploaded PDF file
     */
    const handleRemoveFile = () => {
        setUploadedPdfFile(null);
        setErrorMessage('');
    };

    /**
     * Validates whether or not an input is empty.
     * Returns a error text to fluent ui.
     * @param value The data that requires validation
     */
    const validateInputNotEmpty = (value) => {
        return value === '' ? I18n.get().t('ManualTestDialog_InvalidInput') : '';
    };

    /**
     * Validates if the input fields contain valid data.
     */
    const validateForm = () => {
        if (
            validateInputNotEmpty(orderPositionId) === '' &&
            validateInputNotEmpty(wearerName) === '' &&
            resultLeft !== '' &&
            resultRight !== '' &&
            uploadedPdfFile !== null
        ) {
            setErrorMessage('');
            return true;
        }
        setErrorMessage(I18n.get().t('ManualTestDialog_ValidationError'));
        return false;
    };

    /**
     * Submit the hearing test result.
     */
    const submitTestResult = async () => {
        setSuccessMessage('');
        setErrorMessage('');
        if (!validateForm()) {
            return;
        }
        setIsBusy(true);
        const _msalApp = await msalApp();
        const name = _msalApp.getAccount().name;
        const formData = new FormData();
        formData.append('dateExecuted', dateExecuted.toISOString());
        formData.append('isSuccessfulLeft', resultLeft);
        formData.append('isSuccessfulRight', resultRight);
        formData.append('orderPositionId', orderPositionId);
        formData.append('testerName', name);
        if (uploadedPdfFile) {
            formData.append('formFile', uploadedPdfFile);
        }
        try {
            const newTestResult = await postFormDataToApi('v1.0/TestResult/pdf-upload', formData);
            props.updateTableData(newTestResult);
            setSuccessMessage(I18n.get().t('ManualTestDialog_SavedSuccessful'));
            setTimeout(() => closeDialog(), 2000);
        } catch (error) {
            console.error(error);
            setErrorMessage(I18n.get().t('ManualTestDialog_SaveError'));
            setIsBusy(false);
        }
    };

    /**
     * Open the abort dialog when the input states are not default. If abort dialog must not be open, trigger close.
     */
    const openAbortDialog = () => {
        if (
            resultLeft !== '' ||
            resultRight !== '' ||
            uploadedPdfFile !== null
        ) {
            setIsAbortDialogOpen(true);
        } else {
            closeDialog();
        }
    };

    /**
     * Body of the panel
     */
    const onRenderBody = () => {
        return (
            <PanelBody>
                <SubHeadline fontColor={theme.palette.black}>{I18n.get().t('ManualTestDialog_SubHeadline')}</SubHeadline>
                <InputContainer>
                    <TextField
                        styles={{ color: 'black !important' }}
                        validateOnFocusOut
                        validateOnLoad={false}
                        disabled
                        label={I18n.get().t('ManualTestDialog_Position')}
                        defaultValue={orderPositionNumber}
                        placeholder=""
                    />
                    <TextField
                        validateOnFocusOut
                        validateOnLoad={false}
                        onGetErrorMessage={validateInputNotEmpty}
                        disabled
                        label={I18n.get().t('ManualTestDialog_Wearer')}
                        value={wearerName}
                        placeholder=""
                        onChange={(_, newValue) => setWearerName(newValue)}
                    />
                </InputContainer>
                <InputHeadlineContainer>
                    <Label>{I18n.get().t('ManualTestPdfUploadDialog_Title')}</Label>
                </InputHeadlineContainer>
                <FileUploadContainer>
                    {!uploadedPdfFile && (
                        <CustomFileUploadWrapper themePrimary={theme.palette.themePrimary}>
                            <FileUploader
                                handleChange={handlePdfUpload}
                                name="file"
                                types={fileTypes}
                                maxSize={10} // Max size in MB
                                onSizeError={handlePdfMaxSizeError}
                                classes="file-uploader"
                            >
                                <Icon iconName="pdf-upload" className="upload-icon" />
                                {!uploadedPdfFile && (
                                    <SelectedFileInfo>
                                        <UploadText themePrimary={theme.palette.themePrimary}>
                                            {I18n.get().t('ManualTestPdfUploadDialog_Label')}
                                            <span className="upload-text-browse"> {I18n.get().t('ManualTestPdfUploadDialog_LabelBrowse')} </span>
                                        </UploadText>
                                        <UploadSubText>
                                            {I18n.get().t('ManualTestPdfUploadDialog_SizeLabel')}
                                        </UploadSubText>
                                    </SelectedFileInfo>
                                )}
                            </FileUploader>
                        </CustomFileUploadWrapper>
                    )}
                    {uploadedPdfFile && (
                        <DisplayUploadedFileInfo>
                            <Icon iconName="pdf-upload" />
                            <DisplayFileInfo>
                                <UploadText themePrimary={theme.palette.themePrimary}>
                                    {uploadedPdfFile.name}
                                </UploadText>
                                <DisplayFileProperties>
                                    <div className="file-size-info">
                                        {I18n.get().t('ManualTestPdfUploadDialog_Filesize')} {(uploadedPdfFile.size / (1024 * 1024)).toFixed(2)} MB
                                    </div>
                                    <DeleteFileWrapper onClick={handleRemoveFile}>
                                        <Icon iconName="Delete" />
                                        <span className="delete-info" >{I18n.get().t('ManualTestPdfUploadDialog_Delete')}</span>
                                    </DeleteFileWrapper>
                                </DisplayFileProperties>
                            </DisplayFileInfo>
                        </DisplayUploadedFileInfo>
                    )}
                </FileUploadContainer>
                <ManualTestStatusDialog
                    resultLeft={resultLeft}
                    resultRight={resultRight}
                    dateExecuted={dateExecuted}
                    onResultLeftChange={setResultLeft}
                    onResultRightChange={setResultRight}
                    onDateExecutedChange={dateExecutedHandler}
                />
            </PanelBody>
        );
    };

    /**
     * Footer content of the panel.
     */
    const onRenderFooterContent = () => (
        <PanelFooter>
            <MessageWrapper hidden={errorMessage === '' && successMessage === ''}>
                <MessageBar isMultiline={false} messageBarType={successMessage !== '' ? MessageBarType.success : MessageBarType.error}>
                    {successMessage !== '' ? successMessage : errorMessage}
                </MessageBar>
            </MessageWrapper>
            <FlexEnd>
                <BusySpinner isBusy={isBusy} />
                <DefaultButton styles={cancelButtonStyles} onClick={openAbortDialog} text={I18n.get().t('ManualTestDialog_Cancel')} />
                <PrimaryButton disabled={isBusy} onClick={submitTestResult} text={I18n.get().t('ManualTestDialog_Confirm')} />
            </FlexEnd>
        </PanelFooter>
    );

    /**
     * Header content of the panel.
     */
    const onRenderHeader = () => (
        <PanelHeader>
            <Headline fontColor={theme.palette.black}>{I18n.get().t('ManualTestDialog_Headline')}</Headline>
        </PanelHeader>
    );

    return (
        <>
            <Panel
                isOpen={!props.hidden}
                isFooterAtBottom={true}
                onDismiss={openAbortDialog}
                isLightDismiss
                type={PanelType.custom}
                customWidth={'max-content'}
                closeButtonAriaLabel={I18n.get().t('DebtorPanel_Close')}
                onRenderHeader={onRenderHeader}
                onRenderBody={onRenderBody}
                onRenderFooter={onRenderFooterContent}
            />
            <BasicDialog
                hidden={!isAbortDialogOpen}
                title={I18n.get().t('AbortManualTestDialog_Headline')}
                text={I18n.get().t('AbortManualTestDialog_Text')}
                confirmCallback={() => closeDialog()}
                cancelCallback={() => setIsAbortDialogOpen(false)}
            />
        </>
    );
};